import React, { useMemo, useState } from 'react';
import { SparklesIcon } from '@heroicons/react/24/outline';
import { getImportantObjects } from '../../../utils/getImportantObjects';
import WorldObjectDetailModal from './WorldObjectDetailModal';

export default function WorldObjectsPanel({
  worldObject,
  imagePrompt,
  isExpanded,
  currentBook,
  onGenerateCharacterImage,
  onGeneratePlaceImage,
  onGenerateThingImage,
  generatingCharIdx,
  generatingPlaceUid,
  generatingThingUid,
}) {
  const [selectedObject, setSelectedObject] = useState(null);
  const [selectedObjectType, setSelectedObjectType] = useState(null);
  const [generatingDetailModal, setGeneratingDetailModal] = useState(false);

  const importantObjects = useMemo(() => {
    return getImportantObjects(worldObject);
  }, [worldObject]);

  if (!isExpanded) return null;

  const renderObjectList = (objects, title, type) => {
    if (!objects || objects.length === 0) return null;

    const getGeneratingState = (obj) => {
      if (type === 'character') return generatingCharIdx === (obj._uid || obj.id);
      if (type === 'place') return generatingPlaceUid === (obj._uid || obj.id);
      if (type === 'thing') return generatingThingUid === (obj._uid || obj.id);
      return false;
    };

    const handleGenerate = (obj) => {
      if (type === 'character') onGenerateCharacterImage?.(obj._uid || obj.id);
      if (type === 'place') onGeneratePlaceImage?.(obj._uid || obj.id);
      if (type === 'thing') onGenerateThingImage?.(obj._uid || obj.id);
    };

    return (
      <div className="mb-6">
        <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3">
          {title} ({objects.length})
        </h4>
        <div className="grid grid-cols-2 gap-3">
          {objects.map((obj, idx) => {
            const name = obj.name || obj.title || 'Unknown';
            const hasImage = !!obj.referenceImage;
            const isGenerating = getGeneratingState(obj);

            // Extract all details information
            let allDetailsData = [];
            if (obj.details && typeof obj.details === 'object') {
              allDetailsData = Object.entries(obj.details)
                .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                .map(([sectionKey, sectionValue]) => ({
                  section: sectionKey.replace(/_/g, ' '),
                  items: typeof sectionValue === 'object' && sectionValue !== null
                    ? Object.entries(sectionValue)
                        .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                        .map(([k, v]) => ({
                          label: k.replace(/_/g, ' '),
                          value: (() => {
                            if (typeof v === 'string') return v;
                            if (typeof v === 'object' && v !== null) {
                              return Object.values(v).filter(val => typeof val === 'string').join(', ');
                            }
                            return String(v);
                          })()
                        }))
                    : []
                }));
            }

            // Handle description that might be a string or object
            let descriptionText = '';
            if (typeof obj.description === 'string') {
              descriptionText = obj.description;
            } else if (typeof obj.description === 'object' && obj.description) {
              // If description is an object, try to extract a meaningful string
              const descObj = obj.description;
              if (descObj.summary) {
                descriptionText = descObj.summary;
              } else if (descObj.overview) {
                descriptionText = descObj.overview;
              } else {
                // Get first non-empty string value from object
                descriptionText = Object.values(descObj)
                  .find(v => typeof v === 'string' && v.length > 0) || '';
              }
            }

            return (
              <div
                key={idx}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex flex-col h-64"
                onClick={() => {
                  setSelectedObject(obj);
                  setSelectedObjectType(type);
                }}
              >
                {/* Image Container */}
                <div className="relative bg-gray-100 h-24 flex items-center justify-center flex-shrink-0">
                  {hasImage ? (
                    <img
                      src={obj.referenceImage}
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="text-gray-400 text-center">
                      <div className="text-xl mb-0.5">📷</div>
                      <p className="text-xs">No image</p>
                    </div>
                  )}
                  {hasImage && (
                    <div className="absolute top-1 right-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                      ✓
                    </div>
                  )}
                </div>

                {/* Info Container */}
                <div className="p-2 flex flex-col flex-1 overflow-hidden">
                  <p className="font-bold text-gray-900 text-sm truncate flex-shrink-0" title={name}>
                    {name}
                  </p>
                  
                  {/* Scrollable Details */}
                  {allDetailsData.length > 0 && (
                    <div className="mt-1 overflow-y-auto flex-1 text-xs space-y-2 pr-1">
                      {allDetailsData.map((section, sectionIdx) => (
                        <div key={sectionIdx}>
                          <p className="font-semibold text-indigo-600 text-xs">{section.section}</p>
                          <div className="space-y-0.5 ml-0">
                            {section.items.map((item, itemIdx) => (
                              <div key={itemIdx}>
                                <p className="text-gray-600 font-medium">{item.label}:</p>
                                <p className="text-gray-700 text-xs">{item.value}</p>
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {descriptionText && allDetailsData.length === 0 && (
                    <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                      {descriptionText}
                    </p>
                  )}

                  {/* Generate Button */}
                  <button
                    onClick={() => handleGenerate(obj)}
                    disabled={isGenerating}
                    className="w-full mt-2 px-2 py-1 text-xs font-medium bg-indigo-500 hover:bg-indigo-600 disabled:bg-gray-400 text-white rounded transition-colors flex items-center justify-center gap-1 flex-shrink-0"
                    title={`Generate image for ${name}`}
                  >
                    <SparklesIcon className="h-3 w-3" />
                    {isGenerating ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  const allCharacters = Array.isArray(worldObject?.characters) ? worldObject.characters : [];
  const allPlaces = Array.isArray(worldObject?.places) ? worldObject.places : [];
  const allThings = Array.isArray(worldObject?.things) ? worldObject.things : [];

  const totalObjects =
    allCharacters.length +
    allPlaces.length +
    allThings.length;

  const handleGenerateFromModal = async (obj) => {
    setGeneratingDetailModal(true);
    try {
      if (selectedObjectType === 'character') {
        await onGenerateCharacterImage?.(obj._uid || obj.id);
      } else if (selectedObjectType === 'place') {
        await onGeneratePlaceImage?.(obj._uid || obj.id);
      } else if (selectedObjectType === 'thing') {
        await onGenerateThingImage?.(obj._uid || obj.id);
      }
    } finally {
      setGeneratingDetailModal(false);
    }
  };

  // Separate important objects by type
  const importantCharacters = importantObjects.filter(obj => obj._objectType === 'character');
  const importantPlaces = importantObjects.filter(obj => obj._objectType === 'place');
  const importantThings = importantObjects.filter(obj => obj._objectType === 'thing');

  return (
    <>
      <div className="w-[650px] bg-white border border-gray-200 rounded-lg shadow-lg p-4 max-h-[80vh] overflow-y-auto">
        <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
          World Objects
        </h3>

        {totalObjects === 0 && importantObjects.length === 0 ? (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
            No matching objects found. All world objects will be used.
          </div>
        ) : (
          <div className="text-xs text-gray-600 mb-4 pb-3 border-b border-gray-200">
            {totalObjects + importantObjects.length} object{totalObjects + importantObjects.length !== 1 ? 's' : ''} will be used for image generation
          </div>
        )}

        {/* Important Objects Section */}
        {importantObjects.length > 0 && (
          <div className="mb-6 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <h4 className="text-sm font-semibold text-amber-900 uppercase tracking-wide mb-3">
              ⭐ Important Objects ({importantObjects.length})
            </h4>
            <div className="grid grid-cols-2 gap-3">
              {importantObjects.map((obj, idx) => {
                const name = obj.name || obj.title || 'Unknown';
                const hasImage = !!obj.referenceImage;
                const isGenerating = 
                  (obj._objectType === 'character' && generatingCharIdx === (obj._uid || obj.id)) ||
                  (obj._objectType === 'place' && generatingPlaceUid === (obj._uid || obj.id)) ||
                  (obj._objectType === 'thing' && generatingThingUid === (obj._uid || obj.id));

                // Extract all details information
                let allDetailsData = [];
                if (obj.details && typeof obj.details === 'object') {
                  allDetailsData = Object.entries(obj.details)
                    .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                    .map(([sectionKey, sectionValue]) => ({
                      section: sectionKey.replace(/_/g, ' '),
                      items: typeof sectionValue === 'object' && sectionValue !== null
                        ? Object.entries(sectionValue)
                            .sort(([keyA], [keyB]) => keyA.localeCompare(keyB))
                            .map(([k, v]) => ({
                              label: k.replace(/_/g, ' '),
                              value: (() => {
                                if (typeof v === 'string') return v;
                                if (typeof v === 'object' && v !== null) {
                                  return Object.values(v).filter(val => typeof val === 'string').join(', ');
                                }
                                return String(v);
                              })()
                            }))
                        : []
                    }));
                }

                // Handle description that might be a string or object
                let descriptionText = '';
                if (typeof obj.description === 'string') {
                  descriptionText = obj.description;
                } else if (typeof obj.description === 'object' && obj.description) {
                  const descObj = obj.description;
                  if (descObj.summary) {
                    descriptionText = descObj.summary;
                  } else if (descObj.overview) {
                    descriptionText = descObj.overview;
                  } else {
                    descriptionText = Object.values(descObj)
                      .find(v => typeof v === 'string' && v.length > 0) || '';
                  }
                }

                const typeLabel = obj._objectType === 'character' ? 'Character' : obj._objectType === 'place' ? 'Place' : 'Thing';
                const typeBgColor = obj._objectType === 'character' ? 'bg-blue-100 text-blue-800' : obj._objectType === 'place' ? 'bg-green-100 text-green-800' : 'bg-purple-100 text-purple-800';

                return (
                  <div
                    key={idx}
                    className="bg-white rounded-lg border border-amber-200 overflow-hidden hover:shadow-md transition-shadow cursor-pointer flex flex-col h-64"
                    onClick={() => {
                      setSelectedObject(obj);
                      setSelectedObjectType(obj._objectType);
                    }}
                  >
                    {/* Image Container */}
                    <div className="relative bg-gray-100 h-24 flex items-center justify-center flex-shrink-0">
                      {hasImage ? (
                        <img
                          src={obj.referenceImage}
                          alt={name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="text-gray-400 text-center">
                          <div className="text-xl mb-0.5">📷</div>
                          <p className="text-xs">No image</p>
                        </div>
                      )}
                      {hasImage && (
                        <div className="absolute top-1 right-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs font-medium">
                          ✓
                        </div>
                      )}
                    </div>

                    {/* Info Container */}
                    <div className="p-2 flex flex-col flex-1 overflow-hidden">
                      <div className="flex items-center justify-between gap-2 mb-1">
                        <p className="font-bold text-gray-900 text-sm truncate flex-1" title={name}>
                          {name}
                        </p>
                        <span className={`inline-block text-xs font-medium px-1.5 py-0.5 rounded flex-shrink-0 ${typeBgColor}`}>
                          {typeLabel}
                        </span>
                      </div>
                      
                      {/* Scrollable Details */}
                      {allDetailsData.length > 0 && (
                        <div className="mt-1 overflow-y-auto flex-1 text-xs space-y-2 pr-1">
                          {allDetailsData.map((section, sectionIdx) => (
                            <div key={sectionIdx}>
                              <p className="font-semibold text-indigo-600 text-xs">{section.section}</p>
                              <div className="space-y-0.5 ml-0">
                                {section.items.map((item, itemIdx) => (
                                  <div key={itemIdx}>
                                    <p className="text-gray-600 font-medium">{item.label}:</p>
                                    <p className="text-gray-700 text-xs">{item.value}</p>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      {descriptionText && allDetailsData.length === 0 && (
                        <p className="text-gray-600 text-xs mt-1 line-clamp-2">
                          {descriptionText}
                        </p>
                      )}

                      {/* Generate Button */}
                      <button
                        onClick={() => {
                          if (obj._objectType === 'character') onGenerateCharacterImage?.(obj._uid || obj.id);
                          if (obj._objectType === 'place') onGeneratePlaceImage?.(obj._uid || obj.id);
                          if (obj._objectType === 'thing') onGenerateThingImage?.(obj._uid || obj.id);
                        }}
                        disabled={isGenerating}
                        className="w-full mt-2 px-2 py-1 text-xs font-medium bg-amber-500 hover:bg-amber-600 disabled:bg-gray-400 text-white rounded transition-colors flex items-center justify-center gap-1 flex-shrink-0"
                        title={`Generate image for ${name}`}
                      >
                        <SparklesIcon className="h-3 w-3" />
                        {isGenerating ? 'Generating...' : 'Generate'}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {renderObjectList(allCharacters, 'Characters', 'character')}
        {renderObjectList(allPlaces, 'Places', 'place')}
        {renderObjectList(allThings, 'Things', 'thing')}

        <div className="mt-4 pt-3 border-t border-gray-200">
          <p className="text-xs text-gray-500">
            Click <span className="font-medium">Generate</span> to create an image for any world object.
          </p>
        </div>
      </div>

      {/* World Object Detail Modal */}
      <WorldObjectDetailModal
        isOpen={!!selectedObject}
        onClose={() => {
          setSelectedObject(null);
          setSelectedObjectType(null);
        }}
        worldObject={selectedObject}
        objectType={selectedObjectType}
        onGenerateImage={handleGenerateFromModal}
        isGenerating={generatingDetailModal}
      />
    </>
  );
}
